import { Badge } from '@fluentui/react-components';
import { useTranslation } from 'react-i18next';
import { CheckStatus, Severity } from '../domain/types';
import { statusColors } from '../theme/brandTheme';

interface StatusBadgeProps {
  status: CheckStatus;
}

/** Mapping CheckStatus -> cle i18n status.* (fallback FR si manquant). */
const STATUS_I18N_KEY: Record<CheckStatus, string> = {
  pass:           'status.pass',
  fail:           'status.fail',
  warning:        'status.warning',
  manual:         'status.manual',
  not_applicable: 'status.not_applicable',
  error:          'status.error',
};
const STATUS_FALLBACK: Record<CheckStatus, string> = {
  pass:           'Conforme',
  fail:           'Non conforme',
  warning:        'À améliorer',
  manual:         'Manuel',
  not_applicable: 'N/A',
  error:          'Erreur',
};

/** Badge colore pour le statut d'un controle - aligne sur statusColors. */
export function StatusBadge({ status }: StatusBadgeProps) {
  const { t } = useTranslation();
  const palette =
    status === 'pass'           ? { fg: statusColors.pass,          bg: statusColors.passBg }          :
    status === 'fail'           ? { fg: statusColors.fail,          bg: statusColors.failBg }          :
    status === 'warning'        ? { fg: statusColors.warning,       bg: statusColors.warningBg }       :
    status === 'manual'         ? { fg: statusColors.manual,        bg: statusColors.manualBg }        :
    status === 'not_applicable' ? { fg: statusColors.notApplicable, bg: statusColors.notApplicableBg } :
                                  { fg: statusColors.error,         bg: statusColors.errorBg };

  return (
    <Badge
      appearance="filled"
      style={{
        backgroundColor: palette.bg,
        color:           palette.fg,
        fontWeight:      600,
      }}
    >
      {t(STATUS_I18N_KEY[status], STATUS_FALLBACK[status])}
    </Badge>
  );
}

interface SeverityBadgeProps {
  severity: Severity;
}

const SEVERITY_FALLBACK: Record<Severity, string> = {
  critical: 'Critique',
  high:     'Élevée',
  medium:   'Moyenne',
  low:      'Faible',
};

const severityColors: Record<Severity, { fg: string; bg: string }> = {
  critical: { fg: '#7A1F23', bg: '#FBE3E5' },
  high:     { fg: '#A86317', bg: '#FBEFDD' },
  medium:   { fg: '#2E5570', bg: '#E2ECF3' },
  low:      { fg: '#3F5A48', bg: '#E5EFE9' },
};

export function SeverityBadge({ severity }: SeverityBadgeProps) {
  const { t } = useTranslation();
  const palette = severityColors[severity];
  return (
    <Badge
      appearance="filled"
      style={{
        backgroundColor: palette.bg,
        color:           palette.fg,
        fontWeight:      600,
      }}
    >
      {t(`severity.${severity}`, SEVERITY_FALLBACK[severity])}
    </Badge>
  );
}

interface FamilyBadgeProps {
  family: 'identity' | 'email' | 'data' | 'logs' | 'endpoint' | 'collab' | 'apps';
}

/**
 * Palette couleurs par famille (memes que CyFunMappingPage / radial chart).
 * Le label est lu via i18n (cle familyBadge.*) - fallback FR avec accents.
 */
const familyColors: Record<FamilyBadgeProps['family'], { fg: string; bg: string }> = {
  identity: { fg: '#1B4A86', bg: '#E1ECF8' },
  email:    { fg: '#7A1F5B', bg: '#F8DEEC' },
  data:     { fg: '#1F6E70', bg: '#DCF0F1' },
  logs:     { fg: '#7A4B17', bg: '#FBE9D5' },
  endpoint: { fg: '#3B2A86', bg: '#E3DEF8' },
  collab:   { fg: '#1F5E6E', bg: '#DCEEF1' },
  apps:     { fg: '#2D5F2D', bg: '#DCEFDC' },
};
const FAMILY_FALLBACK: Record<FamilyBadgeProps['family'], string> = {
  identity: 'Identité',
  email:    'Email',
  data:     'Données',
  logs:     'Logs',
  endpoint: 'Endpoint',
  collab:   'Collab',
  apps:     'Apps',
};

export function FamilyBadge({ family }: FamilyBadgeProps) {
  const { t } = useTranslation();
  const c = familyColors[family];
  return (
    <Badge appearance="filled" style={{ backgroundColor: c.bg, color: c.fg, fontWeight: 600 }}>
      {t(`familyBadge.${family}`, FAMILY_FALLBACK[family])}
    </Badge>
  );
}
